import React, { ReactElement } from 'react';
import WidgetRow from '../../../components/widget-row/widget-row.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import ContactWidgetContactInformation
  from './contact-widget-contact-information/contact-widget-contact-information.component';
import ContactWidgetDetails from './contact-widget-details/contact-widget-details.component';
import ContactWidgetStaff from './contact-widget-staff/contact-widget-staff.component';
import ContactWidgetTags from './contact-widget-tags/contact-widget-tags.component';
import ContactWidgetDocuments from './contact-widget-documents/contact-widget-documents.component';
import ContactWidgetActivity from './contact-widget-activity/contact-widget-activity.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import { CONTACT_TYPE_COMPANY } from '../../../constants/entity/contact-types';

const ContactSummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isSaving,
    isSaveDisabled,
    isRightDrawerOpen,
    isCommentSaving,
    isContactLinkedToUser,
    isContactPhotoDeleted,
    isRelatedContactListLoading,
    formError,
    currentUser,
    queryCommentId,
    contactData,
    contactPersonData,
    contactCompanyData,
    contactUserData,
    contactPhotoFile,
    contactPhotoPreviewUrl,
    contactAddressItems,
    contactAddressNewItemIds,
    contactAddressDetailsItems,
    contactAssetTypes,
    contactType,
    contactTypeList,
    contactGroupList,
    companyContactPersonList,
    commentLabelList,
    contactCompanyList,
    contactLinkedUserList,
    relatedContactList,
    relatedContactsTotal,
    assetTypeList,
    tagList,
    notLinkedUserList,
    onCancelForm,
    onSubmitForm,
    onUserChange,
    onTagClick,
    onTagsChange,
    onTagAddClick,
    onTagDeleteClick,
    onTagSearch,
    onTagSearchClose,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onCommentEntityLinkClick,
    onAssetTypesChange,
    onContactGroupsChange,
    onContactPhotoFileChange,
    onContactPhotoFileDelete,
    onPhoneChange,
    onPhoneWorkChange,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onEmailChange,
    onWebsiteChange,
    onContactConnectByEmail,
    onContactConnectByWebsite,
    onContactCompanyClick,
    onContactRowClick,
    onContactTypeChange,
    onNotLinkedUserSearch,
    onNotLinkedUserSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onEditModeButtonClick,
    onContactAddressAddNewItem,
    onContactAddressDeleteItemClick,
    onContactAddressDetailsOpen,
    onContactAddressDetailsClose,
    onCreateButtonClick,
    onRelatedContactsChange,
    onRelatedContactListLoadBatchData,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactVCardDownloadButtonClick,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const formPrefix: string = 'contactData';
  const isEmpty: boolean = !contactData;

  return (
    <SinglePageTab
      isDark
      isEmpty={isEmpty}
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <FlexContainer isSpread withNoAlign isColumn={isSmallScreen}>
        <WidgetRow isColumn={!isSmallScreen}>
          {
            (isEditMode || (contactData && contactData.type === CONTACT_TYPE_COMPANY)) && (
              <ContactWidgetStaff
                isLoading={isRelatedContactListLoading}
                isEditMode={isEditMode}
                formPrefix={formPrefix}
                contactData={contactData}
                companyContactPersonList={companyContactPersonList}
                relatedContactList={relatedContactList}
                relatedContactsTotal={relatedContactsTotal}
                onContactRowClick={onContactRowClick}
                onEditModeButtonClick={onEditModeButtonClick}
                onCreateButtonClick={onCreateButtonClick}
                onRelatedContactsChange={onRelatedContactsChange}
                onContactPersonSearch={onContactPersonSearch}
                onContactPersonSearchClose={onContactPersonSearchClose}
                onLoadBatchData={onRelatedContactListLoadBatchData}
              />
            )
          }
          <ContactWidgetContactInformation
            isEditMode={isEditMode}
            formPrefix={formPrefix}
            contactData={contactData}
            contactType={contactType}
            contactPersonData={contactPersonData}
            contactCompanyData={contactCompanyData}
            contactAddressItems={contactAddressItems}
            contactAddressNewItemIds={contactAddressNewItemIds}
            contactAddressDetailsItems={contactAddressDetailsItems}
            relatedContactList={relatedContactList}
            onPhoneChange={onPhoneChange}
            onPhoneWorkChange={onPhoneWorkChange}
            onAddressFieldKeyDown={onAddressFieldKeyDown}
            onAddressSearchCompleted={onAddressSearchCompleted}
            onEmailChange={onEmailChange}
            onWebsiteChange={onWebsiteChange}
            onContactConnectByEmail={onContactConnectByEmail}
            onContactConnectByWebsite={onContactConnectByWebsite}
            onEditModeButtonClick={onEditModeButtonClick}
            onContactAddressAddNewItem={onContactAddressAddNewItem}
            onContactAddressDeleteItemClick={onContactAddressDeleteItemClick}
            onContactAddressDetailsOpen={onContactAddressDetailsOpen}
            onContactAddressDetailsClose={onContactAddressDetailsClose}
            onContactVCardDownloadButtonClick={onContactVCardDownloadButtonClick}
          />
          <ContactWidgetDetails
            isEditMode={isEditMode}
            isContactLinkedToUser={isContactLinkedToUser}
            isContactPhotoDeleted={isContactPhotoDeleted}
            formPrefix={formPrefix}
            contactData={contactData}
            contactType={contactType}
            contactUserData={contactUserData}
            contactPhotoFile={contactPhotoFile}
            contactPhotoPreviewUrl={contactPhotoPreviewUrl}
            currentUser={currentUser}
            contactAssetTypes={contactAssetTypes}
            assetTypeList={assetTypeList}
            contactTypeList={contactTypeList}
            contactCompanyList={contactCompanyList}
            notLinkedUserList={notLinkedUserList}
            contactGroupList={contactGroupList}
            onUserChange={onUserChange}
            onAssetTypesChange={onAssetTypesChange}
            onNotLinkedUserSearch={onNotLinkedUserSearch}
            onNotLinkedUserSearchClose={onNotLinkedUserSearchClose}
            onContactCompanySearch={onContactCompanySearch}
            onContactCompanySearchClose={onContactCompanySearchClose}
            onContactTypeChange={onContactTypeChange}
            onContactGroupsChange={onContactGroupsChange}
            onContactCompanyClick={onContactCompanyClick}
            onContactPhotoFileChange={onContactPhotoFileChange}
            onContactPhotoFileDelete={onContactPhotoFileDelete}
            onEditModeButtonClick={onEditModeButtonClick}
          />
        </WidgetRow>
        <WidgetRow isColumn={!isSmallScreen}>
          <ContactWidgetTags
            isEditMode={isEditMode}
            contactData={contactData}
            formPrefix={formPrefix}
            tagList={tagList}
            onTagClick={onTagClick}
            onTagsChange={onTagsChange}
            onTagAddClick={onTagAddClick}
            onTagDeleteClick={onTagDeleteClick}
            onTagSearch={onTagSearch}
            onTagSearchClose={onTagSearchClose}
            onEditModeButtonClick={onEditModeButtonClick}
          />
          {
            !isEditMode && contactData && contactData.attachments && !!contactData.attachments.length && (
              <ContactWidgetDocuments
                contactData={contactData}
              />
            )
          }
          {
            !isEditMode && (
              <ContactWidgetActivity
                isCommentSaving={isCommentSaving}
                contactData={contactData}
                currentUser={currentUser}
                queryCommentId={queryCommentId}
                commentLabelList={commentLabelList}
                contactLinkedUserList={contactLinkedUserList}
                tagList={tagList}
                onAddComment={onAddComment}
                onUpdateComment={onUpdateComment}
                onDeleteComment={onDeleteComment}
                onCommentEntityLinkClick={onCommentEntityLinkClick}
              />
            )
          }
        </WidgetRow>
      </FlexContainer>
    </SinglePageTab>
  );
});

export default ContactSummary;
